import React from 'react';
import {
  FEATURE_ALIGNMENT_CONTROL,
  FEATURE_BORDER_RADIUS_CONTROL,
  FEATURE_DIMENSIONS_CONTROL,
  FEATURE_OPACITY_CONTROL,
  FEATURE_POSITION_CONTROL,
  FEATURE_ROTATION_CONTROL,
} from '../flags';
import { ImageItem } from '../items/image/image-item-type';
import { AccordionHeader } from '@crabs-ai/ds-react';
import { AlignmentControls } from './controls/alignment-controls';
import { BorderRadiusControl } from './controls/border-radius-controls';
import { DimensionsControls } from './controls/dimensions-controls';
import { OpacityControls } from './controls/opacity-controls';
import { PositionControl } from './controls/position-control';
import { RotationControl } from './controls/rotation-controls';
import { AnimationControls } from './controls/animation-controls';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';
import { TimingControls } from './controls/timing-controls';
import { useTranslations } from 'next-intl';

const IMG_DEFAULTS = { time: true, positionShape: true };

const ImgInspectorUnmemoized: React.FC<{
  item: ImageItem;
  showFillControls?: boolean;
  isMulti?: boolean;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({
  item,
  showFillControls = true,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
}) => {
  const t = useTranslations();
  const { openSections, toggleSection } = useInspectorSections({
    itemId: item.id,
    defaults: IMG_DEFAULTS,
    collapseAllTrigger,
    expandAllTrigger,
  });

  return (
    <div className="layout-padding-bottom">
      <div className="flex flex-col gap-2xs">
        {/* Animation 섹션 */}
        <div className="border-b border-sys-style-neutral-light">
          <AnimationControls
            itemId={item.id}
            animations={item.animations ?? EMPTY_ANIMATIONS}
            durationInFrames={item.durationInFrames}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.timing.title')}
            isOpened={openSections.time}
            onClick={() => toggleSection('time')}
          />
          <CollapsibleContent isOpen={openSections.time}>
            <div className="px-md py-4xs">
              <TimingControls itemId={item.id} />
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title="위치 및 모양"
            isOpened={openSections.positionShape}
            onClick={() => toggleSection('positionShape')}
          />
          <CollapsibleContent isOpen={openSections.positionShape}>
            <div className="flex flex-col gap-4xs px-md py-4xs">
              {FEATURE_ALIGNMENT_CONTROL && (
                <AlignmentControls itemId={item.id} />
              )}
              {FEATURE_POSITION_CONTROL && <PositionControl itemId={item.id} />}
              {FEATURE_DIMENSIONS_CONTROL && (
                <DimensionsControls itemId={item.id} />
              )}
              {FEATURE_ROTATION_CONTROL && (
                <RotationControl
                  rotation={item.rotation}
                  itemId={item.id}
                />
              )}
              {showFillControls && (
                <div className={'flex w-full justify-between gap-4xs'}>
                  {FEATURE_OPACITY_CONTROL && (
                    <div className={'w-full'}>
                      <OpacityControls
                        opacity={item.opacity}
                        itemId={item.id}
                      />
                    </div>
                  )}
                  {FEATURE_BORDER_RADIUS_CONTROL && (
                    <BorderRadiusControl
                      borderRadius={item.borderRadius}
                      itemId={item.id}
                    />
                  )}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </div>
  );
};

export const ImgInspector = React.memo(ImgInspectorUnmemoized);
